import type { UseFormReturnType } from '@mantine/form';
import { Divider, NumberInput, SimpleGrid, Stack, Text } from '@mantine/core';
import type { ManualVobFormInput, VobBenefitGroup } from '@unify/fhir-model/schemas';

function usedAmount(total?: number, remaining?: number): number | undefined {
  if (total === undefined || remaining === undefined) return undefined;
  return Math.max(total - remaining, 0);
}

function SectionHeading({ children }: { children: string }): JSX.Element {
  return (
    <Text size="xs" c="dimmed" fw={700} tt="uppercase" lts={0.5}>
      {children}
    </Text>
  );
}

function BenefitGroupFields({
  form,
  groupKey,
}: {
  form: UseFormReturnType<ManualVobFormInput>;
  groupKey: 'inNetwork' | 'outOfNetwork';
}): JSX.Element {
  const group: VobBenefitGroup = form.values[groupKey] ?? {};
  const set = (field: keyof VobBenefitGroup, value: number | undefined): void => {
    form.setFieldValue(groupKey, { ...group, [field]: value });
  };
  const deductibleUsed = usedAmount(group.deductibleTotal, group.deductibleRemaining);
  const oopUsed = usedAmount(group.oopMaxTotal, group.oopMaxRemaining);
  const visitsUsed = usedAmount(group.visitLimitTotal, group.visitLimitRemaining);

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="Copay"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          value={group.copay ?? ''}
          onChange={(v) => set('copay', v === '' ? undefined : Number(v))}
        />
        <NumberInput
          label="Coinsurance"
          suffix="%"
          min={0}
          max={100}
          value={group.coinsurance ?? ''}
          onChange={(v) => set('coinsurance', v === '' ? undefined : Number(v))}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="Deductible total"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          value={group.deductibleTotal ?? ''}
          onChange={(v) => set('deductibleTotal', v === '' ? undefined : Number(v))}
        />
        <NumberInput
          label="Deductible remaining"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          description={deductibleUsed !== undefined ? `Used: $${deductibleUsed.toLocaleString()}` : undefined}
          value={group.deductibleRemaining ?? ''}
          onChange={(v) => set('deductibleRemaining', v === '' ? undefined : Number(v))}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="OOP max total"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          value={group.oopMaxTotal ?? ''}
          onChange={(v) => set('oopMaxTotal', v === '' ? undefined : Number(v))}
        />
        <NumberInput
          label="OOP max remaining"
          prefix="$"
          min={0}
          decimalScale={2}
          thousandSeparator=","
          description={oopUsed !== undefined ? `Used: $${oopUsed.toLocaleString()}` : undefined}
          value={group.oopMaxRemaining ?? ''}
          onChange={(v) => set('oopMaxRemaining', v === '' ? undefined : Number(v))}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="Visit limit total"
          min={0}
          allowDecimal={false}
          value={group.visitLimitTotal ?? ''}
          onChange={(v) => set('visitLimitTotal', v === '' ? undefined : Number(v))}
        />
        <NumberInput
          label="Visit limit remaining"
          min={0}
          allowDecimal={false}
          description={visitsUsed !== undefined ? `Used: ${visitsUsed.toLocaleString()}` : undefined}
          value={group.visitLimitRemaining ?? ''}
          onChange={(v) => set('visitLimitRemaining', v === '' ? undefined : Number(v))}
        />
      </SimpleGrid>
    </Stack>
  );
}

export function ManualVobBenefitFields({
  form,
}: {
  form: UseFormReturnType<ManualVobFormInput>;
}): JSX.Element {
  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <SectionHeading>In network</SectionHeading>
        <BenefitGroupFields form={form} groupKey="inNetwork" />
      </Stack>
      <Divider />
      <Stack gap="xs">
        <SectionHeading>Out of network</SectionHeading>
        <BenefitGroupFields form={form} groupKey="outOfNetwork" />
      </Stack>
    </Stack>
  );
}
